import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';
import SyncOutlined from '@ant-design/icons/SyncOutlined';
import { useQueryClient } from '@tanstack/react-query';
import Button from 'antd/es/button';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import Spin from 'antd/es/spin';
import { type ColumnType } from 'antd/es/table';
import Tag from 'antd/es/tag';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import { type Conversations, type ConversationsQuery } from '@mai/types';
import { type Conversation } from '@mai/types';

import TableHeader from '../TableHeader';

import Table from '@components/Table';
import {
  CONVERSATIONS_QUERY_KEY,
  useConversationsQuery,
} from '@queries/conversations';
import { renderDate } from '@utils/dates';
import { formatNumber } from '@utils/numbers';

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ConversationsTableHeader = ({
  dataSource,
  pageSize,
  isLoading: _isLoading,
  isFetching,
  size,
  totalLength,
}: {
  dataSource: Conversations;
  pageSize: number;
  isLoading: boolean;
  isFetching: boolean;
  size: 'small' | 'middle' | 'large';
  totalLength?: number;
}) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states
  const conversationTitleFilterValue = useMemo(
    () => searchParams.get('titleLike'),
    [searchParams],
  );

  const pageSizeFilterValue = useMemo(() => {
    const rawValue = searchParams.get('pageSize');
    if (!rawValue || rawValue === '') {
      return 10;
    }
    return parseInt(rawValue, 10) ?? 10;
  }, [searchParams]);
  const setPageSizeFilter = (value: number) => {
    setSearchParams(
      (prev) => {
        if (value) {
          prev.set('pageSize', value.toString());
        } else {
          prev.delete('pageSize');
        }
        return prev;
      },
      { replace: true },
    );
  };
  const pageFilterValue = useMemo(() => {
    const rawValue = searchParams.get('page');
    if (!rawValue || rawValue === '') {
      return 1;
    }
    return parseInt(rawValue, 10) ?? 1;
  }, [searchParams]);
  const setPageFilter = (value: number) => {
    setSearchParams(
      (prev) => {
        prev.set('page', value.toString());
        return prev;
      },
      { replace: true },
    );
  };

  // Local state for search input (for UI only)
  const [searchValue, setSearchValue] = useState(
    conversationTitleFilterValue ?? '',
  );

  // On debounce sync to the URL
  const [debouncedSearchValue] = useDebounce(searchValue, 500);
  useEffect(() => {
    if (debouncedSearchValue === conversationTitleFilterValue) {
      return;
    }
    setSearchParams(
      (prev) => {
        if (debouncedSearchValue) {
          prev.set('titleLike', debouncedSearchValue);
        } else {
          prev.delete('titleLike');
        }
        return prev;
      },
      { replace: true },
    );
  }, [conversationTitleFilterValue, debouncedSearchValue, setSearchParams]);

  return (
    <TableHeader>
      <HeaderContainer>
        <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
          <Input
            placeholder="Search for conversations by title..."
            allowClear
            size={size}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <ControlsContainer>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: '0.25rem',
              marginLeft: '0.75rem',
            }}
          >
            {totalLength !== undefined ? (
              <Tag
                style={{
                  fontSize: size === 'small' ? '0.75rem' : '0.8rem',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                }}
              >
                {formatNumber(totalLength, true)} Conversation
                {totalLength === 1 ? '' : 's'}
              </Tag>
            ) : (
              <Spin />
            )}
          </div>
          <Select
            options={[
              { label: '10', value: '10' },
              { label: '25', value: '25' },
              { label: '50', value: '50' },
              { label: '100', value: '100' },
            ]}
            size={size}
            value={pageSizeFilterValue}
            suffixIcon={<>/ Page</>}
            onChange={(value) => {
              setPageSizeFilter(value);
            }}
            style={{ width: '5rem' }}
          />
          <Button
            size={size}
            disabled={pageFilterValue <= 1}
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              setPageFilter(pageFilterValue - 1);
            }}
          />
          <Button
            size={size}
            disabled={dataSource.length < pageSize}
            icon={<ArrowRightOutlined />}
            onClick={() => {
              setPageFilter(pageFilterValue + 1);
            }}
          />
          <Button
            size={size}
            disabled={isFetching}
            icon={<SyncOutlined spin={isFetching} />}
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [CONVERSATIONS_QUERY_KEY],
              });
            }}
          />
        </ControlsContainer>
      </HeaderContainer>
    </TableHeader>
  );
};

const ConversationsTable = ({
  size = 'middle',
  showHeader = true,
  projectId,
  teamId,
}: {
  size?: 'small' | 'middle' | 'large';
  showHeader?: boolean;
  projectId: string;
  teamId: string;
}) => {
  const [searchParams] = useSearchParams();

  // Filters
  const conversationTitleFilterValue = useMemo(
    () => searchParams.get('titleLike'),
    [searchParams],
  );
  const pageSizeFilterValue = useMemo(() => {
    const rawValue = searchParams.get('pageSize');
    if (!rawValue || rawValue === '') {
      return 10;
    }
    return parseInt(rawValue, 10) ?? 10;
  }, [searchParams]);
  const pageFilterValue = useMemo(() => {
    const rawValue = searchParams.get('page');
    if (!rawValue || rawValue === '') {
      return 1;
    }
    return parseInt(rawValue, 10) ?? 1;
  }, [searchParams]);

  const filters: ConversationsQuery = {
    projectIds: projectId ? [projectId] : undefined,
    titleLike: conversationTitleFilterValue || undefined,
    page: pageFilterValue,
    pageSize: pageSizeFilterValue,
    orderBy: 'createdAt',
    orderDirection: 'desc',
  };

  const conversationsQuery = useConversationsQuery(filters, !!projectId);
  const dataSource = conversationsQuery.data ?? [];

  const columns: ColumnType<Conversation>[] = [
    {
      title: 'Title',
      dataIndex: 'title',
      render: (value, row) => (
        <Link
          to={`/team/${teamId}/project/${projectId}/conversations/${row.conversationId}`}
        >
          {value}
        </Link>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (value) => <span>{renderDate(value)}</span>,
    },
  ];

  return (
    <>
      {showHeader && (
        <ConversationsTableHeader
          dataSource={dataSource}
          pageSize={pageSizeFilterValue}
          isLoading={conversationsQuery.isLoading}
          isFetching={conversationsQuery.isFetching}
          size={size}
          totalLength={dataSource.length}
        />
      )}
      <div style={{ display: 'flex', overflow: 'hidden' }}>
        <Table<Conversation>
          columns={columns}
          loading={conversationsQuery.isLoading}
          dataSource={dataSource}
          size={size}
          rowKey="conversationId"
          pagination={false}
          scroll={{ x: 700 }}
        />
      </div>
    </>
  );
};
export default ConversationsTable;
